import * as Yup from 'yup';

export type YupValidatorOptions = {
  errorMessage?: string;
  isRequired?: boolean;
};

export class YupValidator {
  public static readonly noBlankSpaceRegEx: RegExp = /^(?!\s+$).*/;
  public static readonly localeRegEx: RegExp = /[a-zA-Z]{2}-[a-zA-Z]{2}/;
  public static readonly minOneLowercaseMatcherRegEx: RegExp =
    /^(?=.*[a-z]).+$/;
  public static readonly minOneUppercaseMatcherRegEx: RegExp =
    /^(?=.*[A-Z]).+$/;
  public static readonly minOneNumberMatcherRegEx: RegExp = /^(?=.*\d).+$/;
  public static readonly min8CharactersLengthMatcherRegEx: RegExp = /^.{8,}$/;

  public static readonly validateNonEmptyString = (
    options?: YupValidatorOptions,
  ): Yup.StringSchema =>
    Yup.string()
      .trim()
      .matches(
        YupValidator.noBlankSpaceRegEx,
        options?.errorMessage || 'No blank spaces allowed',
      );

  public static readonly validateEnum = <T>(requiredEnum: T): Yup.AnySchema => {
    return Yup.mixed<T>().oneOf(Object.values(requiredEnum));
  };

  public static readonly validateEmail = (
    emailLabel?: string,
  ): Yup.StringSchema => {
    return Yup.string().email(`${emailLabel} must be an e-mail`);
  };

  public static readonly validateLocale = (
    options?: YupValidatorOptions,
  ): Yup.StringSchema => {
    return Yup.string()
      .trim()
      .matches(
        YupValidator.localeRegEx,
        options?.errorMessage || 'A 4-digit format is required',
      );
  };

  public static readonly validateUserPassword = (): Yup.StringSchema => {
    return Yup.string()
      .concat(
        Yup.string().matches(
          YupValidator.min8CharactersLengthMatcherRegEx,
          'Must be at least eight characters long',
        ),
      )
      .concat(
        Yup.string().matches(
          YupValidator.minOneLowercaseMatcherRegEx,
          'Must have a lowercase character',
        ),
      )
      .concat(
        Yup.string().matches(
          YupValidator.minOneUppercaseMatcherRegEx,
          'Must have an uppercase character',
        ),
      )
      .concat(
        Yup.string().matches(
          YupValidator.minOneNumberMatcherRegEx,
          'Must include a number',
        ),
      );
  };

  public static addCyclicDependencies = (
    dependencies: Array<string>,
  ): [string, string][] => {
    return dependencies.map((dependency) => [dependency, dependency]);
  };

  public static composeValidatorSchema = <
    T extends Record<string, Yup.AnySchema>,
  >(
    payload: [baseSchema: T, dependencies?: Array<string>],
  ): Yup.ObjectSchema<T> => {
    const [baseSchema, dependencies] = payload;

    return Yup.object().shape(
      baseSchema,
      dependencies
        ? YupValidator.addCyclicDependencies(dependencies)
        : undefined,
    );
  };
}
